import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import {
  IQueryOrder,
  IQueryOrderDetail,
  EOrderStatus,
} from "../../interfaces/IQuery";
import {
  IOrder,
  IResponseOrder,
  IResponseOrderDetail,
} from "../../interfaces/Order";
import { ERole } from "../../interfaces/User/newUser";
import { endDateToApiString, startDateToApiString } from "../../utils/date";

export const getOrder = async (
  {
    order_status,
    order_side,
    is_open,
    page,
    pageSize,
    productId,
    orderBy,
    sortOrder,
    open_date_start,
    open_date_end,
    position_id,
    roleSelected,
    client_id,
  }: IQueryOrder,
  signal?: AbortSignal
) => {
  let params = new URLSearchParams();
  if (!!open_date_start)
    params.append(
      "sending_time_start",
      startDateToApiString(open_date_start) || ""
    );
  if (!!open_date_end)
    params.append("sending_time_end", endDateToApiString(open_date_end) || "");
  if (!!order_status && order_status.length > 0) {
    order_status.forEach((status) =>
      status !== EOrderStatus.ALL ? params.append("order_status", status) : null
    );
  }
  if (!!order_side) params.append("order_side", order_side);
  if (!!position_id) params.append("position_id", position_id.toString());
  if (!!is_open) params.append("is_open", is_open.toString());
  if (!!page) params.append("page", page.toString());
  if (!!pageSize) params.append("pageSize", pageSize.toString());
  if (!!productId && productId.length > 0) {
    productId.forEach((product) =>
      !!product ? params.append("product_id", product.toString()) : null
    );
  }
  if (!!orderBy) params.append("orderBy", orderBy);
  if (!!sortOrder) params.append("sortOrder", sortOrder);
  if (!!client_id) params.append("client_id", client_id);

  const isCustomer = roleSelected === ERole.client;

  const response = await api.get<IResponseOrder>(
    isCustomer
      ? `/web/order-positions/customer`
      : `/web/order-positions/analyst`,
    {
      signal,
      params,
    }
  );
  const serverTimestamp =
    response.headers["last-modified"] || new Date().toISOString();
  const returnData: IResponseOrder = {
    ...response.data,
    lastUpdated: serverTimestamp,
  };

  return returnData;
};

export const useOrder = (params: IQueryOrder) => {
  const { roleSelected } = params;

  return useQuery({
    queryKey: ["order", params],
    queryFn: async ({ signal }) => {
      const data = await getOrder(params, signal);
      return data;
    },
    refetchInterval: 1000 * 60 * 5,
    refetchIntervalInBackground: true,
    keepPreviousData: true,
    enabled: !!roleSelected,
  });
};

export const getCustomerOrderDetail = async (
  { id }: IQueryOrderDetail,
  signal?: AbortSignal
) => {
  const { data } = await api.get<IOrder[]>(
    `/web/order-positions/customer/detail/${id}`,
    {
      signal,
    }
  );
  return data;
};

export const useCustomerOrderDetail = (params: IQueryOrderDetail) => {
  const { id, isAnalytic } = params;

  return useQuery({
    queryKey: ["customerOrderDetail", params],
    queryFn: ({ signal }) => getCustomerOrderDetail(params, signal),
    enabled: !!id && !isAnalytic,
    keepPreviousData: true,
  });
};

export const getAnalystOrderDetail = async (
  {
    id,
    page,
    pageSize,
    sortOrder,
    orderBy,
    order_status,
    client_id,
  }: IQueryOrderDetail,
  signal?: AbortSignal
) => {
  let params = new URLSearchParams();
  if (!!order_status && order_status.length > 0) {
    order_status.forEach((status) =>
      status !== EOrderStatus.ALL ? params.append("order_status", status) : null
    );
  }
  if (!!page) params.append("page", page.toString());
  if (!!pageSize) params.append("pageSize", pageSize.toString());
  if (!!orderBy) params.append("orderBy", orderBy);
  if (!!sortOrder) params.append("sortOrder", sortOrder);
  if (!!client_id) params.append("client_id", client_id);

  const { data } = await api.get<IResponseOrderDetail>(
    `/web/order-positions/analyst/detail/${id}`,
    {
      params,
      signal,
    }
  );

  return data;
};

export const useAnalystOrderDetail = (params: IQueryOrderDetail) => {
  const { id, isAnalytic } = params;

  return useQuery({
    queryKey: ["analystOrderDetail", params],
    queryFn: ({ signal }) => getAnalystOrderDetail(params, signal),
    keepPreviousData: true,
    enabled: !!id && !!isAnalytic,
  });
};

export async function getOrderDownload({
  productId,
  open_date_start,
  open_date_end,
  is_open,
  order_side,
  order_status,
  roleSelected,
  position_id,
  client_id,
}: IQueryOrder) {
  let params = new URLSearchParams();
  if (!!open_date_start)
    params.append(
      "sending_time_start",
      startDateToApiString(open_date_start) || ""
    );
  if (!!open_date_end)
    params.append("sending_time_end", endDateToApiString(open_date_end) || "");

  if (!!order_status && order_status.length > 0)
    params.append("order_status", order_status.toString());
  if (!!order_side) params.append("order_side", order_side);
  if (!!is_open) params.append("is_open", is_open.toString());
  if (!!productId && productId.length > 0) {
    productId.forEach((product) =>
      !!product ? params.append("product_id", product.toString()) : null
    );
  }
  if (!!position_id) params.append("position_id", position_id.toString());
  if (!!client_id) params.append("client_id", client_id);

  const isCustomer = roleSelected === ERole.client;
  try {
    const response = await api.get(
      isCustomer
        ? `/web/order-positions/customer/download`
        : `/web/order-positions/analyst/download`,
      {
        responseType: "blob",
        params,
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      isCustomer ? `ordens-do-cliente.xlsx` : `ordens-do-analista.xlsx`
    );

    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  } catch (error) {
    console.error("Erro ao exportar arquivo:", error);
    throw error;
  }
}
